// Cadastro de Tipo de Peça (issue #4, revisto no ADR-0006). Item do catálogo
// têxtil, com uma Categoria e uma rota de Processos. **Não tem preço** — preço
// mora na Tabela de Preços (ver `precificacao.ts`).

import { newId, type Id } from "./ids.ts";
import type { Snapshot, TipoPeca } from "./snapshot.ts";

export type { TipoPeca };

export interface DadosTipoPeca {
  nome: string;
  categoriaId: Id;
  /** Processos por que a peça passa, em ordem. Opcional — vazio por padrão. */
  processos?: readonly string[];
}

export class ErroTipoPeca extends Error {}

function validar(
  dados: DadosTipoPeca,
  snapshot: Snapshot,
  ignorarId?: Id,
): { nome: string; categoriaId: Id; processos: string[] } {
  const nome = dados.nome.trim();
  if (nome === "") throw new ErroTipoPeca("Informe o nome do tipo de peça.");

  const duplicado = snapshot.tiposPeca.some(
    (t) => t.id !== ignorarId && t.nome.toLowerCase() === nome.toLowerCase(),
  );
  if (duplicado) throw new ErroTipoPeca(`Já existe o tipo de peça "${nome}".`);

  if (!snapshot.categorias.some((c) => c.id === dados.categoriaId)) {
    throw new ErroTipoPeca("Escolha uma categoria válida.");
  }
  const processos = [...(dados.processos ?? [])].map((p) => p.trim()).filter(Boolean);
  return { nome, categoriaId: dados.categoriaId, processos };
}

export function criarTipoPeca(
  snapshot: Snapshot,
  dados: DadosTipoPeca,
  /** Id gerado no cliente (ADR-0003). Omitido = o domínio gera. */
  id: Id = newId(),
): Snapshot {
  const { nome, categoriaId, processos } = validar(dados, snapshot);
  const tipo: TipoPeca = { id, nome, categoriaId, processos, ativo: true };
  return { ...snapshot, tiposPeca: [...snapshot.tiposPeca, tipo] };
}

export function editarTipoPeca(
  snapshot: Snapshot,
  id: Id,
  dados: DadosTipoPeca,
): Snapshot {
  const atual = snapshot.tiposPeca.find((t) => t.id === id);
  if (!atual) throw new ErroTipoPeca("Tipo de peça não encontrado.");
  const { nome, categoriaId, processos } = validar(dados, snapshot, id);
  return {
    ...snapshot,
    tiposPeca: snapshot.tiposPeca.map((t) =>
      t.id === id
        ? {
            ...t,
            nome,
            categoriaId,
            // se `processos` não veio nos dados, preserva o que já existia
            processos: dados.processos === undefined ? t.processos : processos,
          }
        : t,
    ),
  };
}

/**
 * Tipos de Peça a mostrar na contagem do lançamento de OS deste Cliente:
 * todos os ativos, restritos a `cliente.tiposPecaVisiveis` quando definido.
 * Cliente inexistente cai no comportamento sem restrição (todos os ativos).
 */
export function tiposParaLancamento(
  snapshot: Snapshot,
  clienteId: Id,
): TipoPeca[] {
  const cliente = snapshot.clientes.find((c) => c.id === clienteId);
  const visiveis = cliente?.tiposPecaVisiveis ?? null;
  return snapshot.tiposPeca
    .filter((t) => t.ativo && (visiveis === null || visiveis.includes(t.id)))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

import { saldoBacklog } from "./backlog.ts";
import { loteEstaFaturado } from "./faturamento.ts";

export function definirAtivoTipoPeca(
  snapshot: Snapshot,
  id: Id,
  ativo: boolean,
): Snapshot {
  if (!snapshot.tiposPeca.some((t) => t.id === id)) {
    throw new ErroTipoPeca("Tipo de peça não encontrado.");
  }

  if (!ativo) {
    const temNaLoteAberta = snapshot.lotes.some(
      (l) => !l.canceladoEm && !loteEstaFaturado(snapshot, l.id) &&
        snapshot.contagens.some(c => c.loteId === l.id && c.tipoId === id)
    );
    if (temNaLoteAberta) {
      throw new ErroTipoPeca("Tipo de peça está sendo usado em OS(s) abertas. Fature ou cancele antes de inativar.");
    }
    const saldo = saldoBacklog(snapshot).filter(l => l.tipoId === id).reduce((acc, l) => acc + l.quantidade, 0);
    if (saldo > 0) {
      throw new ErroTipoPeca(`Tipo de peça possui saldo pendente de ${saldo} peça(s) no Backlog.`);
    }
  }

  return {
    ...snapshot,
    tiposPeca: snapshot.tiposPeca.map((t) =>
      t.id === id ? { ...t, ativo } : t,
    ),
  };
}
